import { ComponentProps } from "react";
import { useWindowSize } from "@hooks/useWindowSize";
import { ImagePresets } from "@pages/Article/components/ArticleThumbnail/utils/enums";
import Card from "@particles/card/Card";
import { CardVariations } from "@particles/card/utils/types";
import { CaaSImageFilters } from "@typings/OptimisedImage";
import cn from "classnames";

export type HomepageTopStoriesProps = {
  /** Individual article */
  cardProps: ComponentProps<typeof Card>;
  /** Index of the article in the list */
  index: number;
};

/**
 * Renders a single story card for the Homepage Top Stories section.
 * https://www.figma.com/design/rmzJoZwUpb3iLm6lMDXJ2W/BT-MASTER--WEB-?node-id=1312-1855&t=g7P8QlYHW3Ymfii6-4
 */
const HomepageTopStories = ({
  cardProps,
  index,
}: HomepageTopStoriesProps): React.ReactElement => {
  const { isScreenLG } = useWindowSize();
  const isLeadArticle = index === 0;
  const isDisplayMediaArticle = [6, 7].includes(index);
  const displayMedia = isDisplayMediaArticle || (isScreenLG && isLeadArticle);

  const leadCardFilters: CaaSImageFilters = {
    w: ImagePresets.LARGE,
    dpr: 1,
    f: "webp",
  };

  const otherCardFilters: CaaSImageFilters = {
    w: ImagePresets.MEDIUM,
    dpr: 1,
    f: "webp",
  };

  const variant: CardVariations = {
    image: {
      position: isDisplayMediaArticle ? "left" : "",
      width: cn({
        "w-3/10": isDisplayMediaArticle,
        "w-full": isLeadArticle,
      }),
      imageClassName: "object-cover aspect-3x2",
      filters: isLeadArticle ? leadCardFilters : otherCardFilters,
      loading: "eager",
    },
    content: {
      width: cn({ "w-7/10": isDisplayMediaArticle }),
      bgColor: cn({ "bg-gray-850": isLeadArticle }),
      updatedTimeColor: isLeadArticle ? "!text-white" : "",
      extraClass: cn({
        "text-center items-center w-full text-white": isLeadArticle,
        "pl-4": isDisplayMediaArticle,
      }),
    },
    kicker: {
      color: "text-gray-515",
    },
    title: {
      size: cn("text-base", { "text-4xl": isLeadArticle }),
      extraClass: "font-lct",
      color: cn("text-gray-850", { "text-white": isLeadArticle }),
    },
  };

  return (
    <div
      className={cn("story text- border-gray-175 lg:order-1 lg:col-span-1", {
        "order-1 lg:row-span-3": isLeadArticle,
        "lg:mb-0": index === 1 || index === 5 || index === 9,
        "lg:border-r lg:pr-3": index < 6,
        "order-2": index === 6,
        "order-3": index === 7,
        "order-4": (index >= 1 && index <= 5) || index > 7,
        "lg:pl-3": index > 1,
      })}
      data-testid="homepage-top-stories-individual-component"
    >
      <Card
        {...cardProps}
        variations={variant}
        className={cn("h-full border-b border-gray-175 pb-3 pt-3", {
          "lg:border-b-0 lg:pb-0": index === 1 || index === 5 || index === 9,
          "lg:pt-0": index === 0 || index === 2 || index === 6,
          "flex-col !flex-nowrap": isLeadArticle,
        })}
        thumbnail={displayMedia ? cardProps.thumbnail : undefined}
        defaultImage={displayMedia ? cardProps.defaultImage : undefined}
      />
    </div>
  );
};

export default HomepageTopStories;
